'use client'

import PropTypes from 'prop-types'
import { useState, useEffect, useCallback } from 'react'
import { useParams, useRouteLoaderData, useNavigate, useLocation } from 'react-router-dom'
import { useUrlStore } from '@repo/store/useUrlStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useLoadingStore } from '@repo/store/useLoadingStore'
import { LoadingState } from '@repo/ui/LoadingState'
import {
  getRouteAndQueryParamsToAddToIframeSrc,
  getIframeLocation,
  getIframeParams,
  lookupRouteToMatchIframeContent,
  replaceMatchingRouteParams,
  triggerPostLoadAction,
  scrollToTop
} from '@repo/utils/reactIframeLoop'

/*
This component does a LOT of work!

 - Primarily, it sets up an iframe to display the .NET content, and displays the correct content based on the React Route by mapping the route name to an aspx page name
 - it then listens for navigation events in the iframe content and rewrites the URL to match a React Route (based on a menuItemId), so that the user can navigate within the iframe and still have the correct URL and use their browser's history
 - it also listens for changes in the clientNo and matterNo cookies, and updates the global store accordingly, so we detect when a client or matter has been changed in the .NET content
 - it also listens for postLoadActions, which are actions that need to be triggered after the iframe content has loaded (e.g. sending an email)
 - it also listens for changes to the iframe content so it can set an appropriate height for the iframe
 - finally, a key feature is that it listens for the iframe content to navigate to the .NET login page, which indicates the user has logged out of the .NET application, and redirects the user to the React logout page
*/

const IframePage = ({ isVisible }) => {
  const params = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    path,
    postLoadAction,
    postLoadActionShouldWaitForIframeToLoad,
    postLoadPage,
    postLoadSelector,
    iframeSrc,
    setIframeSrc,
    setIframeURL,
    routeId
  } = useUrlStore()
  const loaderData = useRouteLoaderData(routeId)
  const { allRoutesByMenuItemId, routesByAspxPageName, routesByPath } = useMenuItemsStore()
  const { updateClientAndMatterFromCookies } = useMattersStore()
  const { databaseSuffix, baseURL, isLocal, sidebarMode } = useGlobalSettingsStore()
  const [iframeHeight, setIframeHeight] = useState(1500)
  const [iframeLoading, setIframeLoading] = useState(false)
  const [contentWindow, setContentWindow] = useState(null)
  const { setIsLoading } = useLoadingStore()
  const { iframeIsLoggedIn, setIframeIsLoggedIn } = useAuthenticationStore()

  const deps = [loaderData, location, params, iframeSrc]
  useEffect(() => {
    // this whole useEffect is responsible for setting the correct iframe src based on the React Route
    // it also listens for changes in the loaderData, which is the data associated with the React Route
    // and updates the iframe src accordingly

    const { hash } = window.location
    const onAuthPage = hash.includes('#/auth/')

    if (onAuthPage) {
      // don't change the iframe page
      return
    }

    console.log('======================== useEffect ========================')
    let newIframeSrc = iframeSrc
    let routeParams = params // these are the route params from the current React Route, imported above
    let routeSpecParams = {} // these will be imported from the route specification
    let urlQueryParams = {} // these are the query params from the current URL

    const route = routesByPath[routeId || '']

    if (route?.menuItemId || route?.menuItemId === 0) {
      // get the menuItemId associated with the React Route, and use it to lookup the aspx page name to render in the iframe
      let { menuItemId, menuItemAreaId, aspxUrlParams, aspxPageName } = route
      const routeDataParams = { Flag: menuItemId, Area: menuItemAreaId }

      if (aspxPageName) {
        // handle special case
        if (
          aspxPageName.indexOf('report') === 0 &&
          aspxPageName.indexOf('reportsnew') === -1 &&
          aspxPageName !== 'reporttypes.aspx'
        ) {
          aspxPageName = `reportlib/${aspxPageName}`
        }

        newIframeSrc = aspxPageName
      }

      if (routeDataParams) {
        routeSpecParams = { ...routeDataParams }
      }

      // add aspxUrlParams values
      if (aspxUrlParams?.length) {
        aspxUrlParams.forEach(({ key, value }) => {
          routeSpecParams[key] = value
        })
      }
    }

    // add any url query params and route params to the iframe src if necessary
    const urlSearchParams = new URLSearchParams(location.search)
    urlSearchParams?.forEach((value, key) => {
      // add the query params
      urlQueryParams[key] = value
    })

    // parse and de-dupe all params
    const combinedParams = getRouteAndQueryParamsToAddToIframeSrc({
      params: {
        ...routeParams,
        ...routeSpecParams,
        ...urlQueryParams
      }
    })

    if (combinedParams?.length && newIframeSrc.includes('.aspx')) {
      if (newIframeSrc.includes('?')) newIframeSrc = newIframeSrc.split('?')[0]
      const queryString = `?${combinedParams.join('&')}`
      // .replace(/ /g, '+')
      // .replace(/=%&/g, '=%25&')
      newIframeSrc += queryString
    }

    const lowerIframeSrc = iframeSrc.toLowerCase()
    const lowerNewIframeSrc = newIframeSrc.toLowerCase()

    console.warn({
      combinedParams,
      iframeSrc,
      newIframeSrc,
      lowerIframeSrc,
      lowerNewIframeSrc,
      routeId,
      route
    })

    if (lowerIframeSrc !== lowerNewIframeSrc) {
      console.log('Updating iframe src', newIframeSrc)
      setIframeSrc(newIframeSrc)
      setIframeLoading(true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => {
    // this hook is automatically called every second to check if the clientNo or matterNo has changed in the cookies
    // we do this because Matter History can update the matter without a navigation event, and we want to stay in sync
    const interval = setInterval(updateClientAndMatterFromCookies, 1000)
    return () => clearInterval(interval)
  }, [path, updateClientAndMatterFromCookies])

  const getContentWindow = useCallback(() => contentWindow, [contentWindow])

  // when the iframe loads a new page (i.e. if the user navigates within the iframe), rewrite the URL to match a React Route
  const onIframeLoad = async ({ target }) => {
    const { contentWindow } = target || {}
    let iframeURL = contentWindow.location
    const { pathname, search } = getIframeLocation({ iframeURL, databaseSuffix })
    const iframePath = pathname
    const iframeSearchParams = search
    const lowerCasePath = iframePath.toLowerCase()
    const hash = window.location.hash
    setIframeURL(iframeURL)
    setContentWindow(contentWindow)

    console.log('======================== onIframeLoad ========================')

    // update iframe content styles
    const mainContent = contentWindow.document.querySelector('#mainContent')
    if (loaderData?.fixedHeight) {
      contentWindow.document.body.classList.add('fixed-height')
      const height = document.body.offsetHeight - 180
      setIframeHeight(Math.round(height))
      if (mainContent) mainContent.style = 'height: 100% !important;'
    } else {
      //set the iframe height to the height of the content
      if (mainContent) mainContent.style = 'height: 0 !important;'
      setTimeout(() => {
        let height = contentWindow?.document?.querySelector('#mainContentPlaceHolderContainer')?.scrollHeight || 2900
        height += 200 // just to be sure
        setIframeHeight(Math.round(height))
        // restore height of #mainContent, which was set to 0 so we could detect its scrollHeight
        if (mainContent) mainContent.style = 'height: 100% !important;'
      }, 300)
    }

    if (!iframeURL.pathname.includes('LoginUsingAuthenticationToken.aspx')) {
      console.warn('Iframe page load: ', iframeURL.pathname + iframeURL.search)
    }

    // handle iframe login event
    if (!iframeIsLoggedIn && lowerCasePath.includes('loginsuccess')) {
      setIsLoading(false)
      setIframeIsLoggedIn(true)

      setIframeSrc('matterpage.aspx?isHome=true')
      updateClientAndMatterFromCookies()
    }

    // handle iframe logout event
    if (lowerCasePath.includes('logoutsuccess')) {
      setIframeIsLoggedIn(false)
      if (!hash.includes('/auth/')) navigate('/auth/logout')
    }

    // handle strange case where iframe src is root. This can happen if Osprey 1 redirects to the referring page
    if (lowerCasePath === `app/` || lowerCasePath === `/${databaseSuffix.toLowerCase()}/app/`) {
      let returnUrl

      const urlSearchParams = new URLSearchParams(location.search)
      urlSearchParams?.forEach((value, key) => {
        if (key.toLowerCase() === 'returnurl') {
          returnUrl = value
        }
      })

      if (returnUrl) {
        const routes = routesByAspxPageName[returnUrl]
        const route = routes?.length ? routes[0] : null
        const routeId = route?.id || null
        const routePath = route?.path
        if (routeId) useUrlStore.setState({ routeId })
        setIframeSrc(`${returnUrl}`)
        if (routePath) navigate(routePath + '?' + urlSearchParams.toString())
      } else {
        setIframeSrc('matterpage.aspx?isHome=true')
      }
    }

    const { iframeParams, menuItemId, menuItemAreaId } = getIframeParams({ iframeURL })

    const { clientNo, matterNo } = updateClientAndMatterFromCookies()

    const matchingRoute = lookupRouteToMatchIframeContent({
      menuItemId,
      menuItemAreaId,
      iframePath,
      iframeSearchParams,
      allRoutesByMenuItemId,
      routesByAspxPageName
    })

    console.log({ matchingRoute })

    let routeWithParams = replaceMatchingRouteParams({ matchingRoute, clientNo, matterNo })

    if (routeWithParams) {
      if (iframeParams && Object.keys(iframeParams).length) {
        routeWithParams = `${routeWithParams}?${Object.entries(iframeParams)
          .map(([key, value]) => `${key}=${value.trim()}`)
          .join('&')}`
      }

      const currentHash = hash.replace('#/', '')

      // route needs to update
      if (routeWithParams !== currentHash && isVisible) {
        setIframeLoading(false)
        if (postLoadAction && postLoadActionShouldWaitForIframeToLoad) {
          triggerPostLoadAction(
            postLoadAction,
            getContentWindow,
            isLocal,
            postLoadActionShouldWaitForIframeToLoad,
            0,
            postLoadPage,
            postLoadSelector
          )
        }

        navigate(`/${routeWithParams}`)
        return
      }
    }

    if (postLoadAction && postLoadActionShouldWaitForIframeToLoad) {
      console.warn({ postLoadAction, postLoadActionShouldWaitForIframeToLoad })

      triggerPostLoadAction(
        postLoadAction,
        getContentWindow,
        isLocal,
        postLoadActionShouldWaitForIframeToLoad,
        0,
        postLoadPage,
        postLoadSelector
      )
    }

    scrollToTop(contentWindow)
    setIframeLoading(false)
  }

  useEffect(() => {
    // i.e. we can trigger the action without waiting for the iframe to load;
    // otherwise, we do it in at the end of the onIframeLoad function
    if (postLoadAction && !postLoadActionShouldWaitForIframeToLoad) {
      triggerPostLoadAction(
        postLoadAction,
        getContentWindow,
        isLocal,
        postLoadActionShouldWaitForIframeToLoad,
        0,
        postLoadPage,
        postLoadSelector
      )
    }
  }, [
    postLoadAction,
    postLoadActionShouldWaitForIframeToLoad,
    getContentWindow,
    postLoadPage,
    postLoadSelector,
    isLocal
  ])

  // don't render the iframe if running locally
  if (isLocal) return null

  const iframeVisibility = !isVisible || iframeLoading || iframeSrc?.includes('OspreyHome.aspx') ? 'none' : 'block'

  return (
    <div style={{ height: isVisible ? '100%' : 0, position: 'relative' }}>
      <iframe
        data-testid="iframe"
        title="Osprey"
        name="ospreyIframe"
        src={`${baseURL}/${iframeSrc || 'matterpage.aspx'}`} // avoid 404s by defaulting to matterpage.aspx. It is important we always render the iframe, so we must not return null if iframeSrc is undefined!
        style={{
          width: sidebarMode === 'expanded' ? 'calc(100% - 404px)' : 'calc(100% - 204px)',
          height: `${iframeHeight}px`,
          minHeight: '1000px',
          border: 'none',
          margin: '0 70px 0 134px',
          marginLeft: sidebarMode === 'expanded' ? '334px' : '134px', // center the iframe
          position: 'relative',
          zIndex: 1,
          display: iframeVisibility,
          transition: 'all 250ms'
        }}
        aria-describedby="loading"
        onLoad={onIframeLoad}
      />

      <div
        style={{
          zIndex: 0,
          position: 'fixed',
          top: '50%',
          left: sidebarMode === 'expanded' ? 'calc(50% + 132px)' : 'calc(50% + 32px)', // add half the sidebar width
          transform: 'translate(-50%, -50%)',
          display: isVisible && iframeLoading && !window.location.hash.includes('auth') ? 'block' : 'none'
        }}
      >
        <LoadingState />
      </div>
    </div>
  )
}

export default IframePage

IframePage.propTypes = {
  isVisible: PropTypes.bool
}
